import { useState, type ReactNode } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { groupOptions, hoistSelectedFirst } from './select-option-groups';

/** Single option row rendered inside {@link SearchSelect}. */
export interface SearchSelectOption<T> {
  /** Stable React key for the row. */
  key: string;
  /** Value committed via `onChange` when the row is chosen. */
  value: T;
  /** Row content — usually a label. */
  label: ReactNode;
  /** Optional leading icon (avatar, provider mark, chevron, …). */
  icon?: ReactNode;
  /** cmdk match/identity string used for search. Defaults to `key`. */
  searchValue?: string;
  /**
   * Optional group. Rows sharing a value render together as a
   * `CommandGroup` with the value as its heading. `undefined`/`''` render
   * a heading-less group; distinct empties bucket separately so a sentinel
   * (e.g. "None") can pin above the rest.
   */
  group?: string;
  /** Optional per-row data-testid on the `CommandItem`. */
  'data-testid'?: string;
}

/**
 * Rendering size for the combobox trigger and list density:
 *
 * - `md` — `h-8` trigger, used inside forms and captioned fields (default).
 *   Matches the old outline `Button` `size="sm"`.
 * - `sm` — `h-7` trigger, used inside toolbars / filter bars alongside chips.
 *   List input, empty copy, and rows stay `text-xs` to match toolbar filters.
 * - `lg` — `h-9` / `text-sm` trigger, for pickers that used to be a default
 *   outline button (`h-9 text-sm`). The command input wrapper follows the
 *   same height so the search row is not taller than the trigger.
 */
export type SearchSelectSize = 'sm' | 'md' | 'lg';

const SIZE_STYLES = {
  sm: {
    trigger: 'h-7 px-2.5 text-xs',
    input: 'h-8 text-xs',
    inputWrapper: 'h-8',
    empty: 'py-3 text-xs',
    item: 'text-xs',
  },
  md: {
    trigger: 'h-8 px-2 text-xs',
    input: 'h-8 text-xs',
    inputWrapper: 'h-8',
    empty: 'py-3 text-xs',
    item: 'text-xs',
  },
  lg: {
    trigger: 'h-9 px-3 text-sm',
    input: 'h-9 text-sm',
    inputWrapper: 'h-9',
    empty: 'py-3 text-sm',
    item: 'text-sm',
  },
} as const;

export interface SearchSelectProps<T> {
  /** Accessible name for the combobox trigger. */
  ariaLabel: string;
  /** `aria-describedby` for the trigger; wire an inline hint (e.g. an "unavailable" warning) to the control. */
  ariaDescribedBy?: string;
  /** Selected-value summary rendered inside the trigger. */
  triggerLabel: ReactNode;
  /** Optional leading icon rendered inside the trigger. */
  triggerIcon?: ReactNode;
  /** Placeholder rendered (muted) when nothing is picked. Wins over `triggerLabel` when `isEmpty` is true. */
  placeholder?: ReactNode;
  /** True when the current value is the empty/"all" sentinel — muted trigger label. */
  isEmpty?: boolean;
  /** Trigger and list density. Defaults to `md` (`h-8`). `lg` is the old default button (`h-9`). */
  size?: SearchSelectSize;
  options: SearchSelectOption<T>[];
  value: T;
  onChange: (next: T) => void;
  /** Called when the popover opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /** Equality for the selected check + reordering. Defaults to `Object.is`. */
  isSelected?: (optionValue: T, value: T) => boolean;
  /** When set, renders a search box with this placeholder. Defaults to "Search…". */
  searchPlaceholder?: string;
  /** Empty-state copy for the search box. Defaults to "No results found.". */
  emptyMessage?: ReactNode;
  /** Hoist the selected row to the top of its group. Defaults to `true`. */
  selectedFirst?: boolean;
  disabled?: boolean;
  /** Extra classes on the trigger button. */
  className?: string;
  /** Extra classes for the popover content surface. */
  contentClassName?: string;
  /** Popover alignment. Defaults to `start`. */
  contentAlign?: 'start' | 'center' | 'end';
  /**
   * Content rendered inside `PopoverContent` in place of the whole `Command`
   * body. Escape hatch for callers that need to swap the surface entirely
   * (e.g. an empty-list explanation with a link CTA).
   */
  overrideBody?: ReactNode;
  'data-testid'?: string;
}

/**
 * Searchable single-select popover with an outline combobox trigger. The
 * canonical "trigger + search + grouped list + trailing check" chrome used by
 * form fields (`h-8`), toolbar filter selects (`h-7`), and the old default
 * outline button (`h-9`, `size="lg"`). Follows {@link ConfigChipSelect}'s option/group model — the
 * chip-triggered sibling — so both share option shapes and selected-first
 * ordering.
 *
 * A sentinel row in its own group stays pinned above the rest even when
 * `selectedFirst` reorders the active option to the top of its group.
 */
export function SearchSelect<T>({
  ariaLabel,
  ariaDescribedBy,
  triggerLabel,
  triggerIcon,
  placeholder,
  isEmpty,
  size = 'md',
  options,
  value,
  onChange,
  onOpenChange,
  isSelected = Object.is,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results found.',
  selectedFirst = true,
  disabled,
  className,
  contentClassName,
  contentAlign = 'start',
  overrideBody,
  'data-testid': dataTestId,
}: SearchSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const density = SIZE_STYLES[size];
  const groups = selectedFirst
    ? hoistSelectedFirst(groupOptions(options), (opt) => isSelected(opt.value, value))
    : groupOptions(options);

  const showPlaceholder = isEmpty === true && placeholder !== undefined;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          data-testid={dataTestId}
          className={cn(
            'w-full justify-between gap-1.5 font-normal',
            density.trigger,
            showPlaceholder && 'text-muted-foreground',
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            {triggerIcon}
            <span className="truncate">{showPlaceholder ? placeholder : triggerLabel}</span>
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={contentAlign}
        className={cn('w-[--radix-popover-trigger-width] min-w-[12rem] p-0', contentClassName)}
      >
        {overrideBody ?? (
          <Command>
            <CommandInput
              placeholder={searchPlaceholder}
              className={density.input}
              wrapperClassName={density.inputWrapper}
            />
            <CommandList className="max-h-72">
              <CommandEmpty className={cn('text-center', density.empty)}>
                {emptyMessage}
              </CommandEmpty>
              {groups.map((group, groupIndex) => (
                <CommandGroup
                  key={group.heading ?? `__group-${groupIndex}`}
                  heading={group.heading}
                >
                  {group.options.map((opt) => {
                    const selected = isSelected(opt.value, value);
                    return (
                      <CommandItem
                        key={opt.key}
                        value={opt.searchValue ?? opt.key}
                        onSelect={() => {
                          onChange(opt.value);
                          setOpen(false);
                          onOpenChange?.(false);
                        }}
                        data-testid={opt['data-testid']}
                        className={cn('gap-2', density.item)}
                      >
                        {opt.icon}
                        <div className="min-w-0 flex-1 truncate">{opt.label}</div>
                        {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" /> : null}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
